import { LockedFeatureConfig, TextOverlayLockedFeatures } from './types';

/**
 * Internal, normalized shape for a locked-feature entry.
 *
 * The public input shape ({@link TextOverlayLockedFeatures}) accepts a
 * `boolean | LockedFeatureConfig` per feature for caller ergonomics. Once it
 * crosses into the provider we collapse it into this uniform record so that
 * consumers never have to narrow the union themselves.
 */
export interface ResolvedLockedFeature {
  disabled: boolean;
  tooltip?: string;
}

/**
 * Normalized version of {@link TextOverlayLockedFeatures} placed on context.
 *
 * Every feature key is required and always resolves to a
 * {@link ResolvedLockedFeature}, defaulting to `{ disabled: false }` when the
 * caller didn't lock the feature.
 */
export type NormalizedLockedFeatures = {
  [K in keyof Required<TextOverlayLockedFeatures>]: ResolvedLockedFeature;
};

const UNLOCKED: ResolvedLockedFeature = { disabled: false };

function resolveLockedFeature(
  config: boolean | LockedFeatureConfig | undefined,
): ResolvedLockedFeature {
  if (!config) {
    return UNLOCKED;
  }

  return {
    disabled: true,
    tooltip: typeof config === 'object' ? config.tooltip : undefined,
  };
}

/**
 * Collapses the public `boolean | LockedFeatureConfig` input shape into the
 * uniform {@link NormalizedLockedFeatures} record consumed by the provider.
 *
 * When a new key is added to {@link TextOverlayLockedFeatures}, TypeScript will
 * require it to be normalized here.
 */
export function normalizeLockedFeatures(
  input?: TextOverlayLockedFeatures,
): NormalizedLockedFeatures {
  return {
    fontFamily: resolveLockedFeature(input?.fontFamily),
  };
}
